import type { ListWorkflowTemplatesResponseJSON } from "@performyard/service-contracts";

import legacyRequest from "../base-request";
import request from "./internal-api/internal-base-request";

export type WorkflowTemplateSortBy = "name" | "last_update" | "todoCount";

export interface ListWorkflowTemplatesParams {
	sortBy?: WorkflowTemplateSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
}

export const getWorkflowTemplates = async (
	params: ListWorkflowTemplatesParams = {}
): Promise<ListWorkflowTemplatesResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));

	const query = searchParams.toString();
	const [response] = await request<ListWorkflowTemplatesResponseJSON>(
		`/v1/workflow-templates${query ? `?${query}` : ""}`,
		{ method: "GET" }
	);
	return response;
};

export type WorkflowTemplateSearchResultJSON = {
	id: string;
	name: string;
};

/**
 * Page size of the step-1 template combobox, and the server's own ceiling for this
 * endpoint (`WorkflowTemplateSearchHandler` clamps `limit` to 20). Matches past this are
 * dropped, which is why the field searches server-side rather than filtering a page.
 */
export const TEMPLATE_SEARCH_LIMIT = 20;

export const searchWorkflowTemplates = async (name: string): Promise<WorkflowTemplateSearchResultJSON[]> => {
	const searchParams = new URLSearchParams();
	if (name) searchParams.set("name", name);
	searchParams.set("limit", String(TEMPLATE_SEARCH_LIMIT));
	const [response] = await legacyRequest<{ json: WorkflowTemplateSearchResultJSON[] }>(
		`/api/workflow-templates/?${searchParams.toString()}`,
		{
			method: "GET",
			// `legacyRequest` sets `Content-Type: application/json` on every call, which makes
			// this handler try to `json.loads` a body that a GET doesn't have. The query string
			// is the real input, so drop the header (the server is guarded too, but a bodyless
			// GET should not be advertising a JSON body in the first place).
			headers: { "Content-Type": "" },
		}
	);
	return response.json;
};

/**
 * Template CRUD goes through the legacy tornado JSON endpoints
 * (`/api/workflow-templates/...`) rather than internal-api: the create path also
 * maintains `company.workflow_templates`, `last_update`, and py_log auditing, and the
 * marshmallow schema owns validation (at-least-one-todo, days >= 1, active-actor
 * checks). Only the list read has moved to internal-api above.
 */

/** A to-do as the legacy API sends and receives it (camelCase on the wire). */
export type WorkflowTemplateTodoJSON = {
	label: string;
	/** A user id, one of the wildcards "self" | "manager" | "other", or null when a signature template is attached. */
	actor: string | null;
	daysToComplete: number;
	/** Signature request template id; sent as `{ id }`, returned as the bare id string. */
	documentSigning: string | { id: string } | null;
};

export type WorkflowTemplateDetailJSON = {
	id: string;
	name: string;
	todos: WorkflowTemplateTodoJSON[];
};

/** `display_user_map`: actor user ids -> display info for hydrating saved templates. */
export type WorkflowTemplateUserMapJSON = Record<string, { name: string; active: boolean }>;

/** `signature_request_map`: signature template ids -> display info for hydrating saved templates. */
export type WorkflowTemplateSignatureMapJSON = Record<string, { name: string; signers: string[] }>;

export type GetWorkflowTemplateResponseJSON = {
	template: WorkflowTemplateDetailJSON;
	display_user_map: WorkflowTemplateUserMapJSON;
	signature_request_map: WorkflowTemplateSignatureMapJSON;
};

export type SaveWorkflowTemplatePayload = {
	name: string;
	todos: WorkflowTemplateTodoJSON[];
};

/** Create/update responses only need the saved template here; the display maps are refetched. */
export type SaveWorkflowTemplateResponseJSON = {
	template: WorkflowTemplateDetailJSON;
};

export const getWorkflowTemplate = async (workflowTemplateId: string): Promise<GetWorkflowTemplateResponseJSON> => {
	const [response] = await legacyRequest<GetWorkflowTemplateResponseJSON>(
		`/api/workflow-templates/${workflowTemplateId}/`,
		{ method: "GET" }
	);
	return response;
};

export const createWorkflowTemplate = async (
	payload: SaveWorkflowTemplatePayload
): Promise<SaveWorkflowTemplateResponseJSON> => {
	const [response] = await legacyRequest<SaveWorkflowTemplateResponseJSON>("/api/workflow-templates/create/", {
		method: "POST",
		body: JSON.stringify(payload),
	});
	return response;
};

export const updateWorkflowTemplate = async (
	workflowTemplateId: string,
	payload: SaveWorkflowTemplatePayload
): Promise<SaveWorkflowTemplateResponseJSON> => {
	const [response] = await legacyRequest<SaveWorkflowTemplateResponseJSON>(
		`/api/workflow-templates/${workflowTemplateId}/`,
		{ method: "POST", body: JSON.stringify(payload) }
	);
	return response;
};

export const deleteWorkflowTemplate = async (workflowTemplateId: string): Promise<void> => {
	await legacyRequest(`/api/workflow-templates/${workflowTemplateId}/`, { method: "DELETE" });
};

/**
 * Flatten a legacy 422 validation payload into user-facing messages. The server responds
 * `{ errors: { "Workflow name": ["..."], "Actor": ["..."] } }` (see `parse_errors`);
 * anything else (HTML error page, network failure) falls back so raw transport errors
 * are never shown verbatim. Used for save and delete alike.
 */
export const describeWorkflowTemplateError = (error: unknown, fallback: string): string => {
	const data = (error as { data?: { errors?: unknown } } | null)?.data;
	const errors = data?.errors;
	if (errors && typeof errors === "object" && !Array.isArray(errors)) {
		const messages = Object.values(errors)
			.flat()
			.filter((message): message is string => typeof message === "string" && message.length > 0);
		if (messages.length > 0) {
			return messages.join(" ");
		}
	}
	return fallback;
};
